import labels from "app/labels";
import * as ko from "knockout";
import { dynamicFieldProperties } from "./dynamicFieldProperties";
import { ui_field_state } from "./ui_field_state";

export class ui_field<T> {
  //
  private _disabled: KnockoutObservable<boolean>;
  currentStep: KnockoutObservable<number>;

  //selected Value in UI
  value: KnockoutObservable<T>;
  isVisible: KnockoutObservable<boolean>;

  isRequired: KnockoutObservable<boolean>;

  label: KnockoutObservable<string>;

  disabled: KnockoutComputed<boolean>;

  hasError: KnockoutObservable<boolean>;
  errorLabel: KnockoutObservable<string>;

  requiredRoles: KnockoutObservableArray<string>;

  maxLength: KnockoutObservable<number>;

  editableInSteps: KnockoutObservableArray<number>;

  // keep the default state
  _defaultState: ui_field_state<T>;

  constructor(item?: dynamicFieldProperties) {
    const defaultState = new ui_field_state<T>(item);

    this.value = ko.observable<T>(defaultState.value);
    this.isVisible = ko.observable<boolean>(defaultState.isVisible);
    this.isRequired = ko.observable<boolean>(defaultState.isRequired);
    this.hasError = ko.observable<boolean>(defaultState.hasError);
    this.errorLabel = ko.observable<string>(defaultState.errorLabel);
    this._disabled = ko.observable<boolean>(defaultState.disabled);
    this.label = ko.observable<string>(defaultState.label);
    this.maxLength = ko.observable(defaultState.maxLength);
    this.requiredRoles = ko.observableArray(defaultState.requiredRoles);
    this.editableInSteps =
      typeof defaultState.editableInSteps === "function"
        ? defaultState.editableInSteps
        : ko.observableArray(defaultState.editableInSteps);
    this._defaultState = defaultState;
    this.currentStep = ko.observable(-1);
    this.maxLength.subscribe(() => {
      this.setMaxLengthError();
    });

    this.disabled = ko.computed(() => {
      if (this.editableInSteps().length == 0) {
        return this._disabled();
      }
      return !this.editableInSteps().includes(this.currentStep());
    });
  }

  setDisabled = (value: boolean) => {
    this._disabled(value);
  };

  clear = (): void => {
    // default
    this.hasError(this._defaultState.hasError);

    //
    this.value(this._defaultState.value);
    this.isVisible(this._defaultState.isVisible);
    this.isRequired(this._defaultState.isRequired);
    this.errorLabel(this._defaultState.errorLabel);
    this._disabled(this._defaultState.disabled);
    if (this._defaultState.label) {
      this.label(this._defaultState.label);
    }
    this.maxLength(this._defaultState.maxLength ?? 0);
    this.requiredRoles(this._defaultState?.requiredRoles ?? []);
    this.editableInSteps(this._defaultState?.editableInSteps ?? []);
    this.setMaxLengthError();
  };

  get isValid(): boolean {
    if (this.isVisible()) {
      const isValid =
        (!this.isRequired() && this.isVisible()) ||
        (this.isRequired() && this.value() && this.isVisible() ? true : false);

      const result = isValid && this.hasAllRolesRequired() && this.hasProperLength();
      this.hasError(!result);
      return result;
    }
    this.hasError(false);
    return true;
  }

  get valueIfVisible(): T {
    return this.isVisible() ? this.value() : null;
  }

  hasProperLength = () => {
    let hasLength = true;

    if (typeof this.value() === "string") {
      const value: any = this.value();
      hasLength = this.maxLength() === 0 || value.length <= this.maxLength();
    }
    return hasLength;
  };

  hasAllRolesRequired = () => {
    let hasAllRolesRequired = true;
    // has the required roles
    if (this.requiredRoles().length > 0) {
      const localeRoles = localStorage.getItem("AccessRoles")?.split(",") ?? [];

      let index = 0;
      this.requiredRoles().forEach((role) => {
        const found = localeRoles.find((r) => r === role);
        if (found) {
          index++;
        }
      });
      hasAllRolesRequired = this.requiredRoles().length === index;
    }
    return hasAllRolesRequired;
  };

  setObservableValue = (obs: KnockoutObservable<T>) => {
    if (obs) {
      this.value = obs;
      this._defaultState.value = obs();
      this.setMaxLengthError();
    }
  };

  private setMaxLengthError = () => {
    if (typeof this.value() === "string" && this.maxLength() > 0) {
      this.value.extend({
        maxLength: {
          params: this.maxLength,
          message: labels.descrWithParams("cc_P_NO_MORE_THAN_CHARS", [this.maxLength()]),
        },
      });
    }
  };
}
